import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { PedidosPage } from './views/Pedidos/PedidosPage';
import { ToastProvider } from './components/ui/ToastProvider';
import reportRoutes from './app/reportRoutes';

// Novos componentes
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import ItemsManagement from './pages/ItemsManagement';
import CombosManagement from './pages/CombosManagement';
import CompanyManagement from './pages/CompanyManagement';
import CompanyCreate from './pages/CompanyCreate';
import DeveloperUserManagement from './pages/DeveloperUserManagement';
import CompanyAdminList from './pages/CompanyAdminList';
import CouponCampaigns from './pages/CouponCampaigns';
import MediaManagement from './pages/MediaManagement';
import WhatsappModulePage from './pages/WhatsappModulePage';
import ProjecoesPage from './pages/ProjecoesPage';
import PedidosAvulsosPage from './pages/PedidosAvulsosPage';
import StockItemsPage from './pages/StockItemsPage';
import StockMovementsPage from './pages/StockMovementsPage';
import StockBalancesPage from './pages/StockBalancesPage';
import RecipesManagement from './pages/RecipesManagement';
import DeliveryModulePage from './pages/DeliveryModulePage';
import DeliveryCitiesPage from './pages/DeliveryCitiesPage';
import DeliveryNeighborhoodsPage from './pages/DeliveryNeighborhoodsPage';
import DeliveryCouriersPage from './pages/DeliveryCouriersPage';
import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import SalesReportPage from './pages/reports/SalesReportPage';
import StockReportPage from './pages/reports/StockReportPage';
import MetricsLeadsPage from './pages/MetricsLeadsPage';
import LoyaltyCampaignsPage from './pages/LoyaltyCampaignsPage';
import LoyaltyCustomersPage from './pages/LoyaltyCustomersPage';
import LoyaltyRewardsPage from './pages/LoyaltyRewardsPage';
import MercadoPagoIntegrationPage from './pages/MercadoPagoIntegrationPage';
import MercadoPagoOAuthCallbackPage from './pages/MercadoPagoOAuthCallbackPage';

// Configuração do React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Tema personalizado do Material UI baseado nas cores da marca, com fundo claro e detalhes em amarelo/laranja
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      // Amarelo forte para botões, links e destaques (cor do texto "Better")
      main: '#FFB300',
      contrastText: '#000000',
    },
    secondary: {
      // Laranja queimado para estados/accent (remetendo ao fogo do hambúrguer)
      main: '#FF6F00',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF', // fundo geral claro
      paper: '#F5F5F5',   // cards/caixas levemente destacadas
    },
    text: {
      primary: '#212121',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#000000',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Router>
            <Routes>
              {/* Rotas públicas */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              {/* Rotas protegidas com layout principal */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Navigate to="/dashboard" replace />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/items"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ItemsManagement />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/recipes"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <RecipesManagement />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports/sales"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <SalesReportPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports/stock"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <StockReportPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/combos"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CombosManagement />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/stock/items"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <StockItemsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/stock/movements"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <StockMovementsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/stock/balances"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <StockBalancesPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pedidos"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PedidosPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pedidos-avulsos"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PedidosAvulsosPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/projecoes"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <ProjecoesPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <UserManagement />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/coupons"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <CouponCampaigns />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/media"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <MediaManagement />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/crm/leads"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <MetricsLeadsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/loyalty/campaigns"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <LoyaltyCampaignsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/loyalty/customers"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <LoyaltyCustomersPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/loyalty/rewards"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <LoyaltyRewardsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/whatsapp"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <WhatsappModulePage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/company"
                element={
                  <ProtectedRoute requiredRole="DEVELOPER">
                    <MainLayout>
                      <CompanyManagement />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/company/new"
                element={
                  <ProtectedRoute requiredRole="DEVELOPER">
                    <MainLayout>
                      <CompanyCreate />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/companies"
                element={
                  <ProtectedRoute requiredRole="DEVELOPER">
                    <MainLayout>
                      <CompanyAdminList />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/delivery"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <DeliveryModulePage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/delivery/cities"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <DeliveryCitiesPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/delivery/couriers"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <DeliveryCouriersPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/delivery/neighborhoods"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <DeliveryNeighborhoodsPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dev-users"
                element={
                  <ProtectedRoute requiredRole="DEVELOPER">
                    <MainLayout>
                      <DeveloperUserManagement />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/integracoes/mercadopago"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MainLayout>
                      <MercadoPagoIntegrationPage />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/admin/integracoes/mercadopago/callback"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <MercadoPagoOAuthCallbackPage />
                  </ProtectedRoute>
                }
              />

              {/* Rota para qualquer caminho não encontrado */}
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Navigate to="/dashboard" replace />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              {reportRoutes.map((route) => (
                <Route key={route.path} path={route.path} element={route.element} />
              ))}
            </Routes>
          </Router>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
